import type { DB_TYPE, EGrammarType } from '@quesmed/types';
import type {
  EBatchType,
  EContentType,
  EDifficultyType,
  EEntitlementType,
  EJobPriority,
  EJobStatus,
  EJobType,
  EMockTestType,
  ENotificationActionType,
  ENotificationType,
  EOsceType,
  EPaceType,
  EProductDuration,
  EProductType,
  EQuestionType,
  ESubSource,
  ETopicType,
} from '@quesmed/types/models';
import type { ES3Folder } from '@quesmed/types/resolvers/mutation/admin';
import type { ESubType } from '@quesmed/types/resolvers/query/admin';
import type { EDateFilter, EProductCategory } from '@/config/constants';
import type { ISelectItem } from '@/core/ui';

type Enums =
  | typeof EBatchType
  | typeof EDifficultyType
  | typeof EEntitlementType
  | typeof EMockTestType
  | typeof ETopicType
  | typeof EOsceType
  | typeof EProductType
  | typeof EContentType
  | typeof EGrammarType
  | typeof ENotificationActionType
  | typeof ENotificationType
  | typeof EPaceType
  | typeof EProductDuration
  | typeof DB_TYPE
  | typeof ES3Folder
  | typeof ESubType
  | typeof ESubSource
  | typeof EDateFilter
  | typeof EProductCategory
  | typeof EQuestionType
  | typeof EJobType
  | typeof EJobStatus
  | typeof EJobPriority;

export const getSelectItem = (inputEnum: Enums, values: (string | number)[]): ISelectItem[] => {
  return values.map((key) => ({
    label: inputEnum[key as never]
      ? (inputEnum[key as never] as string)?.replaceAll('_', ' ')?.toLowerCase()
      : (key as string),
    key,
  }));
};

export const getSelectItemWithDescription = (
  inputEnum: Enums,
  values: Array<{ value: string | number; description: string }>,
): ISelectItem[] => {
  return values.map(({ value, description }) => ({
    label: inputEnum[value as never]
      ? (inputEnum[value as never] as string)?.replaceAll('_', ' ')?.toLowerCase()
      : (value as string),
    description,
    key: value,
  }));
};

export function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\\-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function bytesToMB(bytes: number) {
  return (bytes / (1024 * 1024)).toFixed(3);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}

export function containsFileOrBlob(obj: Record<string, any>) {
  if (obj instanceof File || obj instanceof Blob) {
    return true;
  }

  if (Array.isArray(obj)) {
    return obj.some(containsFileOrBlob);
  }

  if (typeof obj === 'object' && obj !== null) {
    return Object.values(obj).some(containsFileOrBlob);
  }

  return false;
}

export function zigzagSort(arr: Array<{ key: string; value: number }>) {
  const sorted = [...arr].sort((a, b) => a.value - b.value);
  const result = [];

  let left = 0;
  let right = sorted.length - 1;

  while (left <= right) {
    if (left === right) {
      result.push(sorted[right]);
      break;
    }

    result.push(sorted[right--]);
    result.push(sorted[left++]);
  }

  return result;
}
